import useNaicsSectors from "../../../shared/hooks/useNaicsSectors";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Chip,
  Tabs,
  Tab,
  Divider,
  Button,
  Snackbar,
  Alert,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Drawer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  InputAdornment,
  MenuItem,
  TablePagination,
  Checkbox,
  Tooltip,
} from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import EmailIcon from "@mui/icons-material/Email";
import HubIcon from "@mui/icons-material/Hub";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ReplayIcon from "@mui/icons-material/Replay";
import EditIcon from "@mui/icons-material/Edit";
import FactoryIcon from "@mui/icons-material/Factory";
import PublicIcon from "@mui/icons-material/Public";
import GroupsIcon from "@mui/icons-material/Groups";
import CampaignIcon from "@mui/icons-material/Campaign";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { useTradeShowWorkspaces } from "./TradeShowWorkspacesContext";
import AutoImportStatusPanel from "./AutoImportStatusPanel";
import WorkspaceAttendees from "./WorkspaceAttendees";
import { TRADE_SHOW_ATTENDEES_ENABLED } from "./featureFlags";
import { startImportStatusPolling } from "./autoImportPolling";
import UploadExhibitorListModal from "./UploadExhibitorListModal";
import {
  formatBroadSectorLabel,
} from "../../bre/companies/BRECompanyHelpers";
import { US_STATES, CANADA_PROVINCES } from "../../../utils/States";
import {
  filterExhibitorsBySearch,
  getDirectoryImportErrorMessage,
  getWorkspaceDirectoryUrl,
} from "./directoryImportUtils";
import { COUNTRY_MAP } from "../../../utils/Countries";
import { PRIMARY_BUTTON_SX } from "../../../theme/buttonstyles";
import api from "../../../services/api/axios";
import { SIGNAL_OPTIONS } from "../prospect-list-generator/ProspectGenerator";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const PAGE_BG = "#f7f9fc";
const TSA_DEBUG = process.env.REACT_APP_TSA_DEBUG === "true";

function logTradeShowAgent(message, details) {
  if (!TSA_DEBUG) return;
  console.info(`[TradeShowAgent] ${message}`, details || "");
}

const STATUS_CHIP = {
  Setup: { bg: "rgba(0,51,108,0.08)", color: BRAND_BLUE },
  Active: { bg: "rgba(100,131,21,0.12)", color: "#648315" },
  Archived: { bg: "#f0f2f5", color: "#8a9ab0" },
};

const WORKSPACE_META_TEXT_SX = {
  fontSize: "0.875rem",
  color: BRAND_BLUE,
  fontWeight: 600,
};

const WORKSPACE_META_LABEL_SX = {
  fontWeight: 700,
  color: BRAND_GREEN,
};

const PANEL_SX = {
  border: "1px solid #dce4ef",
  borderRadius: 2,
  backgroundColor: "#ffffff",
  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
};

const TABS = ["Overview", "Exhibitors", "Briefs", "Email Drafts", "Tasks", "Agent Runs"];
const SCORE_FILTERS = ["All", "Tier A", "Tier B", "Tier C", "Needs Review", "Duplicates"];
const EMPTY_COMMUNITY_PROFILE = {
  targetIndustries: [],
  targetGeographies: [],
  preferredCompanySize: [],
  preferredGrowthSignals: [],
};
const COMPANY_SIZE_OPTIONS = [
  "1-10 employees",
  "11-50 employees",
  "51-200 employees",
  "201-500 employees",
  "501-1,000 employees",
  "1,001-5,000 employees",
  "5,000+ employees",
];
const TARGET_GEOGRAPHY_REGIONS = [
  {
    name: "Northeast Region",
    states: [
      "Connecticut",
      "Maine",
      "Massachusetts",
      "New Hampshire",
      "New Jersey",
      "New York",
      "Pennsylvania",
      "Rhode Island",
      "Vermont",
    ],
  },
  {
    name: "Southeast Region",
    states: [
      "Alabama",
      "Arkansas",
      "Florida",
      "Georgia",
      "Kentucky",
      "Louisiana",
      "Mississippi",
      "North Carolina",
      "South Carolina",
      "Tennessee",
      "Virginia",
      "West Virginia",
    ],
  },
  {
    name: "Midwest Region",
    states: [
      "Illinois",
      "Indiana",
      "Iowa",
      "Kansas",
      "Michigan",
      "Minnesota",
      "Missouri",
      "Nebraska",
      "North Dakota",
      "Ohio",
      "South Dakota",
      "Wisconsin",
    ],
  },
  {
    name: "Southwest Region",
    states: ["Arizona", "New Mexico", "Oklahoma", "Texas"],
  },
  {
    name: "West Region",
    states: [
      "Alaska",
      "California",
      "Colorado",
      "Hawaii",
      "Idaho",
      "Montana",
      "Nevada",
      "Oregon",
      "Utah",
      "Washington",
      "Wyoming",
    ],
  },
  {
    name: "Great Lakes Region",
    states: [
      "Illinois",
      "Indiana",
      "Michigan",
      "Minnesota",
      "New York",
      "Ohio",
      "Pennsylvania",
      "Wisconsin",
    ],
  },
];
const OTHER_COUNTRY_OPTIONS = Object.values(COUNTRY_MAP)
  .filter((country) => country && !["United States", "Canada"].includes(country))
  .sort((a, b) => a.localeCompare(b));
const TARGET_GEOGRAPHY_OPTION_GROUPS = [
  {
    label: "United States",
    options: [
      "United States",
      ...TARGET_GEOGRAPHY_REGIONS.map((region) => region.name),
      ...US_STATES.map((state) => state.name),
    ],
  },
  {
    label: "Canada",
    options: ["Canada", ...CANADA_PROVINCES.map((province) => province.name)],
  },
  {
    label: "Other Countries",
    options: OTHER_COUNTRY_OPTIONS,
  },
];
const TARGET_GEOGRAPHY_OPTIONS = TARGET_GEOGRAPHY_OPTION_GROUPS.flatMap((group) => group.options);
const TARGET_GEOGRAPHY_GROUP_BY_OPTION = new Map(
  TARGET_GEOGRAPHY_OPTION_GROUPS.flatMap((group) => group.options.map((option) => [option, group.label])),
);
const COMMUNITY_PROFILE_FIELDS = [
  {
    key: "targetIndustries",
    label: "Target Industries",
    helperText: "Sector this community wants to attract",
    icon: FactoryIcon,
    accent: "#00336C",
    type: "sector",
  },
  {
    key: "targetGeographies",
    label: "Target Geographies",
    helperText: "Regions or markets to prioritize",
    icon: PublicIcon,
    accent: "#0F766E",
    type: "geography",
    options: TARGET_GEOGRAPHY_OPTIONS,
  },
  {
    key: "preferredCompanySize",
    label: "Preferred Company Size",
    helperText: "Employee range that best fits",
    icon: GroupsIcon,
    accent: "#6D28D9",
    type: "select",
    options: COMPANY_SIZE_OPTIONS,
  },
  {
    key: "preferredGrowthSignals",
    label: "Growth Signals",
    helperText: "Expansion signals to prioritize",
    icon: CampaignIcon,
    accent: "#B45309",
    type: "select",
    options: SIGNAL_OPTIONS.map((option) => option.code),
    formatOption: (value) =>
      SIGNAL_OPTIONS.find((option) => option.code === value)?.name || value,
  },
];
const EXHIBITOR_COLUMNS = [
  { key: "companyName", label: "Company Name" },
  { key: "boothNumber", label: "Booth Number" },
  { key: "website", label: "Website" },
  { key: "industry", label: "Industries" },
  { key: "employeeRange", label: "Employees" },
  { key: "scoreTotal", label: "Score" },
  { key: "tier", label: "Tier" },
];
const EXHIBITOR_DETAIL_FIELDS = [
  { key: "companyName", label: "Company Name" },
  { key: "website", label: "Website" },
  { key: "boothNumber", label: "Booth Number" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "country", label: "Country" },
  { key: "industry", label: "Industry" },
  { key: "employeeRange", label: "Employee Range" },
  { key: "estimatedEmployeeCount", label: "Estimated Employee Count" },
  { key: "description", label: "Description", wide: true },
  { key: "scoreTotal", label: "Score" },
  { key: "tier", label: "Tier" },
  { key: "scoreRationale", label: "Score Rationale", wide: true },
  { key: "growthSignalEvidence", label: "Verified Growth Signals", wide: true },
  { key: "recommendedAction", label: "Recommended Action", wide: true },
];
const EXHIBITOR_DETAIL_ACTIONS = [
  { label: "Generate Brief", icon: <DescriptionIcon /> },
  { label: "Generate Outreach Draft", icon: <EmailIcon /> },
  { label: "Match to CRM", icon: <HubIcon /> },
  { label: "Create Task", icon: <TaskAltIcon /> },
];
const BRIEF_SECTIONS = [
  { key: "companyOverview", label: "Company Overview" },
  { key: "whyThisCompanyFits", label: "Why This Company Fits" },
  { key: "growthExpansionSignals", label: "Growth / Expansion Signals" },
  { key: "conversationStarters", label: "Conversation Starters" },
  { key: "questionsToAsk", label: "Questions to Ask" },
  { key: "recommendedNextAction", label: "Recommended Next Action" },
];
const DRAFT_TYPE_GENERAL = "General Email";
const DRAFT_TYPE_TAILORED = "Tailored Email";
const EXHIBITORS_PER_PAGE = 50;
const EXHIBITOR_TABLE_CELL_SX = {
  color: BRAND_BLUE,
  maxWidth: 260,
  whiteSpace: "nowrap",
};
const PROFILE_SELECTED_CHIP_SX = {
  backgroundColor: BRAND_BLUE,
  color: "white",
  fontWeight: 600,
  height: 22,
  "& .MuiChip-label": {
    px: 0.75,
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

function cleanValue(value) {
  if (Array.isArray(value)) return value.map(cleanValue).filter(Boolean).join(", ");
  return String(value ?? "").trim().replace(/\s+/g, " ");
}

function formatDetailValue(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join(", ") || "-";
  if (Number.isFinite(value)) return value;
  return value || "-";
}

function formatProfileDisplayValue(field, value) {
  if (Array.isArray(value) && field?.formatOption) {
    return value.map((item) => field.formatOption(item)).filter(Boolean).join(", ") || "-";
  }
  return formatDetailValue(value);
}

function formatGrowthSignalEvidence(value) {
  if (!Array.isArray(value) || !value.length) return "-";
  return value
    .map((item) =>
      [
        item.signalType || item.signalCode,
        item.signalDate,
        item.summary,
        item.sourceUrl,
      ]
        .filter(Boolean)
        .join(" | ")
    )
    .filter(Boolean)
    .join("\n");
}

function formatExhibitorDetailValue(field, exhibitor) {
  if (field.key === "growthSignalEvidence") {
    return formatGrowthSignalEvidence(exhibitor[field.key]);
  }
  return formatDetailValue(exhibitor[field.key]);
}

function formatResearchStage(value) {
  return String(value || "")
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function normalizeProfileArray(value) {
  if (Array.isArray(value)) return value.map(cleanValue).filter(Boolean);
  const cleaned = cleanValue(value);
  if (COMPANY_SIZE_OPTIONS.includes(cleaned)) return [cleaned];
  return cleaned
    .split(/[,;\n]/)
    .map((term) => term.trim())
    .filter(Boolean);
}

function filterExhibitors(exhibitors, filter) {
  if (filter === "Duplicates") return exhibitors.filter((exhibitor) => exhibitor.isDuplicate);
  if (filter === "All") return exhibitors;
  return exhibitors.filter((exhibitor) => exhibitor.tier === filter);
}

function formatDateTime(value) {
  if (!value) return "-";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatBriefList(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join("\n");
  return cleanValue(value);
}

function buildBriefFromResponse(exhibitor, generatedBrief) {
  const generatedAt = new Date().toISOString();

  return {
    id: `brief-${exhibitor.id}-${Date.now()}`,
    exhibitorId: exhibitor.id,
    companyName: exhibitor.companyName || "Unnamed Exhibitor",
    tier: exhibitor.tier || "Unscored",
    score: Number.isFinite(exhibitor.scoreTotal) ? exhibitor.scoreTotal : null,
    status: "Generated",
    generatedAt,
    aiGenerated: true,
    rawBrief: generatedBrief,
    sections: {
      companyOverview: generatedBrief.companyOverview || "",
      whyThisCompanyFits: generatedBrief.fitAssessment || "",
      growthExpansionSignals: formatBriefList(generatedBrief.growthSignals),
      conversationStarters: formatBriefList(generatedBrief.conversationStarters),
      questionsToAsk: formatBriefList(generatedBrief.questionsToAsk),
      recommendedNextAction: generatedBrief.recommendedNextAction || "",
    },
  };
}

function buildGeneralEmailDraft(workspace, communityProfile) {
  const generatedAt = new Date().toISOString();
  const tradeShowName = workspace?.tradeShowName || workspace?.workspaceName || "the trade show";
  const valueProposition = "our community's business support, workforce, sites, and partner network";
  const targetGeographies = cleanValue(communityProfile?.targetGeographies) || "your expansion priorities";

  return {
    id: `draft-general-${workspace?.id || "workspace"}-${Date.now()}`,
    exhibitorId: null,
    companyName: "All target exhibitors",
    draftType: DRAFT_TYPE_GENERAL,
    subjectLine1: `Meeting at ${tradeShowName}`,
    subjectLine2: `Opportunities in ${targetGeographies}`,
    emailBody:
      "Hi,\n\n" +
      `I saw your team is exhibiting at ${tradeShowName} and wanted to connect. ` +
      `Our community supports companies evaluating growth opportunities with ${valueProposition}.\n\n` +
      "Would you be open to a brief meeting during the show to compare your current priorities with what our community can support?\n\n" +
      "Best,\n[Your Name]",
    status: "Draft",
    generatedAt,
  };
}

function buildTailoredEmailDraftForExhibitor(exhibitor, communityProfile, brief) {
  const generatedAt = new Date().toISOString();
  const companyName = exhibitor.companyName || "Unnamed Exhibitor";
  const valueProposition = "our community's business support, workforce, sites, and partner network";
  const targetGeographies = cleanValue(communityProfile?.targetGeographies) || "your expansion priorities";
  const briefFit = brief?.sections?.whyThisCompanyFits || `${companyName} appears relevant based on uploaded exhibitor data.`;
  const eventContext = [exhibitor.boothNumber ? `booth ${exhibitor.boothNumber}` : "", exhibitor.tier || ""]
    .filter(Boolean)
    .join(", ");

  return {
    id: `draft-tailored-${exhibitor.id}-${Date.now()}`,
    exhibitorId: exhibitor.id,
    companyName,
    draftType: DRAFT_TYPE_TAILORED,
    subjectLine1: `Meeting at the show: ${companyName}`,
    subjectLine2: `${companyName} and opportunities in ${targetGeographies}`,
    emailBody:
      `Hi ${companyName} team,\n\n` +
      `I noticed ${companyName}${eventContext ? ` (${eventContext})` : ""} while reviewing exhibitors for the trade show. ` +
      `${briefFit}\n\n` +
      `Based on what we know so far, ${valueProposition} may be relevant as you evaluate growth opportunities, partnerships, or future market presence.\n\n` +
      `Would you be open to a brief meeting during the show to compare your current priorities with what our community can support?\n\n` +
      `Best,\n[Your Name]`,
    status: "Draft",
    generatedAt,
  };
}

function hasGeneralEmailDraft(drafts) {
  return drafts.some((draft) => draft.draftType === DRAFT_TYPE_GENERAL);
}

function hasTailoredEmailDraft(drafts, exhibitorId) {
  return drafts.some(
    (draft) => draft.exhibitorId === exhibitorId && draft.draftType === DRAFT_TYPE_TAILORED
  );
}

function hasRequiredEmailDrafts(drafts, exhibitorId) {
  return hasGeneralEmailDraft(drafts) && hasTailoredEmailDraft(drafts, exhibitorId);
}

function isRequestTimeout(error) {
  return (
    error?.code === "ECONNABORTED" ||
    error?.name === "CanceledError" ||
    /timeout/i.test(String(error?.message || ""))
  );
}

function getBriefGenerationErrorMessage(error) {
  if (isRequestTimeout(error)) {
    return "Brief generation is taking longer than expected. The request timed out, but the workflow may still be processing.";
  }
  return (
    error?.response?.data?.detail ||
    error?.message ||
    "Unable to generate company brief."
  );
}

function getAgentRunChipSx(status) {
  if (status === "Failed") {
    return {
      backgroundColor: "rgba(211,47,47,0.12)",
      color: "#D32F2F",
    };
  }
  if (status === "Completed") {
    return {
      backgroundColor: "rgba(100,131,21,0.12)",
      color: BRAND_GREEN,
    };
  }
  return {
    backgroundColor: "rgba(0,51,108,0.08)",
    color: BRAND_BLUE,
  };
}

function mergeGeneratedEmailDrafts(currentDrafts, generatedDrafts, replacedTailoredExhibitorIds) {
  const generatedGeneral = generatedDrafts.find((draft) => draft.draftType === DRAFT_TYPE_GENERAL);
  const existingGeneral = currentDrafts.find((draft) => draft.draftType === DRAFT_TYPE_GENERAL);
  const generalDraft = generatedGeneral || existingGeneral;
  const generatedTailoredDrafts = generatedDrafts.filter((draft) => draft.draftType === DRAFT_TYPE_TAILORED);

  return [
    ...(generalDraft ? [generalDraft] : []),
    ...generatedTailoredDrafts,
    ...currentDrafts.filter(
      (draft) =>
        draft.draftType !== DRAFT_TYPE_GENERAL &&
        !(draft.draftType === DRAFT_TYPE_TAILORED && replacedTailoredExhibitorIds.has(draft.exhibitorId))
    ),
  ];
}

export default function WorkspaceDetailPage() {
  const { options: sectorOptions } = useNaicsSectors();
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    getWorkspace,
    loading: workspacesLoading,
    loadWorkspaces,
    setWorkspaceExhibitorPreview,
    setWorkspaceCommunityProfile,
    setWorkspaceBriefs,
    setWorkspaceDrafts,
    setWorkspaceAgentRuns,
  } =
    useTradeShowWorkspaces();
  const [activeTab, setActiveTab] = useState(0);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadSuccessOpen, setUploadSuccessOpen] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [scoreFilter, setScoreFilter] = useState("All");
  const [exhibitorPage, setExhibitorPage] = useState(0);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileForm, setProfileForm] = useState(EMPTY_COMMUNITY_PROFILE);
  const [selectedExhibitorId, setSelectedExhibitorId] = useState(null);
  const [selectedBriefId, setSelectedBriefId] = useState(null);
  const [selectedDraftId, setSelectedDraftId] = useState(null);
  const [briefGenerationExhibitorId, setBriefGenerationExhibitorId] = useState(null);
  const [briefGenerationError, setBriefGenerationError] = useState("");
  const [importStatus, setImportStatus] = useState(null);
  const [directoryUrl, setDirectoryUrl] = useState("");
  const [directoryPreviewLoading, setDirectoryPreviewLoading] = useState(false);
  const [directoryImportError, setDirectoryImportError] = useState("");
  const [importSummary, setImportSummary] = useState(null);
  const [scoringLoading, setScoringLoading] = useState(false);
  const [scoringError, setScoringError] = useState("");
  const [researchStatus, setResearchStatus] = useState(null);
  const [exhibitorSearch, setExhibitorSearch] = useState("");
  const [scoreSortDescending, setScoreSortDescending] = useState(false);
  const autoDraftAttemptedIdsRef = useRef(new Set());
  const mountedRef = useRef(true);
  const generateBriefControllerRef = useRef(null);
  const directoryPreviewControllerRef = useRef(null);
  const scoreExhibitorsControllerRef = useRef(null);

  const workspace = getWorkspace(id);
  const communityProfile = workspace?.communityProfile ?? null;
  const exhibitorPreview = workspace?.exhibitorPreview ?? null;
  const exhibitors = useMemo(() => exhibitorPreview?.exhibitors ?? [], [exhibitorPreview]);
  const briefs = useMemo(() => workspace?.briefs ?? [], [workspace?.briefs]);
  const drafts = useMemo(() => workspace?.drafts ?? [], [workspace?.drafts]);
  const displayDrafts = useMemo(() => mergeGeneratedEmailDrafts(drafts, [], new Set()), [drafts]);
  const agentRuns = useMemo(() => workspace?.agentRuns ?? [], [workspace?.agentRuns]);

  useEffect(() => {
    mountedRef.current = true;
    logTradeShowAgent("workspace detail mounted", { id });
    return () => {
      logTradeShowAgent("workspace detail unmounted; cleanup started", { id });
      mountedRef.current = false;
      generateBriefControllerRef.current?.abort();
      directoryPreviewControllerRef.current?.abort();
      scoreExhibitorsControllerRef.current?.abort();
      logTradeShowAgent("workspace detail cleanup completed", { id });
    };
  }, [id]);

  useEffect(() => {
    if (!id) return undefined;
    return startImportStatusPolling({
      workspaceId: id,
      fetchStatus: async (workspaceId, signal) => {
        logTradeShowAgent("import status request started", { workspaceId });
        const response = await api.get(`/api/trade-show-agent/workspaces/${workspaceId}/import-status`, {
          signal,
        });
        logTradeShowAgent("import status request completed", { workspaceId, status: response.status });
        return response.data;
      },
      onStatus: setImportStatus,
      onComplete: (nextStatus) => {
        if (nextStatus?.hasImportJob) loadWorkspaces();
      },
      onFailure: (_error, { consecutiveFailures } = {}) => {
        if (consecutiveFailures < 2) return;
        setImportStatus((current) => {
          if (!current?.hasImportJob) return current;
          return {
            ...current,
            status: current.status || "pending",
            currentStage: "status_unavailable",
            progressPercent: current?.progressPercent || 0,
            safeErrorMessage: "Automatic import status is temporarily unavailable. Retrying status checks...",
            terminal: false,
          };
        });
      },
    });
  }, [id, loadWorkspaces]);

  useEffect(() => {
    if (!workspace?.id || researchStatus?.hasResearchJob) return undefined;
    let canceled = false;
    const controller = new AbortController();
    async function loadResearchStatus() {
      try {
        logTradeShowAgent("research status initial request started", { workspaceId: workspace.id });
        const response = await api.get(`/api/trade-show-agent/workspaces/${workspace.id}/exhibitors/enrich-status`, {
          signal: controller.signal,
        });
        if (!canceled) {
          const nextStatus = response.data?.status || null;
          setResearchStatus(nextStatus);
          if (nextStatus?.hasResearchJob && !nextStatus.terminal) {
            setScoringLoading(true);
          }
          logTradeShowAgent("research status initial request completed", { workspaceId: workspace.id });
        }
      } catch (error) {
        if (controller.signal.aborted) {
          logTradeShowAgent("research status initial request aborted", { workspaceId: workspace.id });
          return;
        }
        if (!canceled) setResearchStatus(null);
        logTradeShowAgent("research status initial request failed", {
          workspaceId: workspace.id,
          status: error?.response?.status,
          message: error?.message,
        });
      }
    }
    loadResearchStatus();
    return () => {
      canceled = true;
      controller.abort();
    };
  }, [workspace?.id, researchStatus?.hasResearchJob]);

  useEffect(() => {
    if (!workspace?.id || !researchStatus?.hasResearchJob || researchStatus.terminal) return undefined;
    let canceled = false;
    let timeoutId = null;
    let controller = null;

    const pollResearchStatus = async () => {
      controller = new AbortController();
      try {
        logTradeShowAgent("research status poll started", { workspaceId: workspace.id });
        const response = await api.get(`/api/trade-show-agent/workspaces/${workspace.id}/exhibitors/enrich-status`, {
          signal: controller.signal,
        });
        if (canceled) return;
        const nextStatus = response.data?.status || null;
        setResearchStatus(nextStatus);
        if (nextStatus?.terminal) {
          setScoringLoading(false);
          await loadWorkspaces();
          return;
        }
        logTradeShowAgent("research status poll completed", { workspaceId: workspace.id });
      } catch (error) {
        if (canceled) return;
        if (controller.signal.aborted) {
          logTradeShowAgent("research status poll aborted", { workspaceId: workspace.id });
          return;
        }
        setScoringError(error?.response?.data?.detail || error?.message || "Unable to check exhibitor research status.");
        logTradeShowAgent("research status poll failed", {
          workspaceId: workspace.id,
          status: error?.response?.status,
          message: error?.message,
        });
      }
      timeoutId = window.setTimeout(pollResearchStatus, 3000);
    };

    timeoutId = window.setTimeout(pollResearchStatus, 1000);
    return () => {
      canceled = true;
      controller?.abort();
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [workspace?.id, researchStatus?.hasResearchJob, researchStatus?.terminal, loadWorkspaces]);

  useEffect(() => {
    if (!workspace) return;
    setDirectoryUrl((current) => current || getWorkspaceDirectoryUrl(workspace));
  }, [workspace]);

  useEffect(() => {
    if (!workspace?.id || !exhibitors.length || !briefs.length) return undefined;

    const briefsByExhibitorId = new Map(briefs.map((brief) => [brief.exhibitorId, brief]));
    const tierAWithoutEmailDrafts = exhibitors.filter(
      (exhibitor) =>
        exhibitor?.id &&
        exhibitor.tier === "Tier A" &&
        briefsByExhibitorId.has(exhibitor.id) &&
        !hasRequiredEmailDrafts(drafts, exhibitor.id) &&
        !autoDraftAttemptedIdsRef.current.has(exhibitor.id)
    );
    if (!tierAWithoutEmailDrafts.length) return undefined;

    const generatedDrafts = [
      buildGeneralEmailDraft(workspace, communityProfile),
      ...tierAWithoutEmailDrafts.map((exhibitor) => {
        autoDraftAttemptedIdsRef.current.add(exhibitor.id);
        return buildTailoredEmailDraftForExhibitor(exhibitor, communityProfile, briefsByExhibitorId.get(exhibitor.id));
      }),
    ];
    const generatedTailoredExhibitorIds = new Set(
      tierAWithoutEmailDrafts.map((exhibitor) => exhibitor.id)
    );

    setWorkspaceDrafts(workspace.id, (currentDrafts) =>
      mergeGeneratedEmailDrafts(currentDrafts, generatedDrafts, generatedTailoredExhibitorIds)
    );
    return undefined;
  }, [workspace, exhibitors, briefs, drafts, communityProfile, setWorkspaceDrafts]);

  if (workspacesLoading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!workspace) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography sx={{ color: "#8a9ab0" }}>Workspace not found.</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/trade-show-agent")}
          sx={{ mt: 2, textTransform: "none", color: BRAND_BLUE }}
        >
          Back to Trade Show Agent
        </Button>
      </Box>
    );
  }

  const chipStyle = STATUS_CHIP[workspace.status] ?? STATUS_CHIP.Setup;
  const hasCommunityProfile = Boolean(communityProfile);
  const targetProfileReady = Boolean(
    normalizeProfileArray(communityProfile?.targetIndustries).length &&
      normalizeProfileArray(communityProfile?.targetGeographies).length &&
      normalizeProfileArray(communityProfile?.preferredCompanySize).length &&
      normalizeProfileArray(communityProfile?.preferredGrowthSignals).length
  );
  const filledProfileFieldCount = hasCommunityProfile
    ? COMMUNITY_PROFILE_FIELDS.filter((field) => cleanValue(communityProfile[field.key])).length
    : 0;
  const totalExhibitors = exhibitors.length;
  const duplicateCount = exhibitors.filter((exhibitor) => exhibitor.isDuplicate).length;
  const tierACount = exhibitors.filter((exhibitor) => exhibitor.tier === "Tier A").length;
  const tierBCount = exhibitors.filter((exhibitor) => exhibitor.tier === "Tier B").length;
  const needsReviewCount = exhibitors.filter((exhibitor) => exhibitor.tier === "Needs Review").length;
  const excludedCount = exhibitors.filter((exhibitor) => exhibitor.tier === "Excluded").length;
  const scoredCount = exhibitors.filter((exhibitor) => Number.isFinite(exhibitor.scoreTotal)).length;
  const missingWebsiteCount = exhibitors.filter(
    (exhibitor) => !cleanValue(exhibitor.website) && !cleanValue(exhibitor.websiteDomain)
  ).length;
  const uniqueExhibitors = totalExhibitors - duplicateCount;
  const tierAExhibitors = exhibitors
    .filter((exhibitor) => exhibitor.tier === "Tier A")
    .sort((a, b) => (b.scoreTotal || 0) - (a.scoreTotal || 0));
  const tierBExhibitors = exhibitors
    .filter((exhibitor) => exhibitor.tier === "Tier B")
    .sort((a, b) => (b.scoreTotal || 0) - (a.scoreTotal || 0));
  const briefedExhibitorIds = new Set(briefs.map((brief) => brief.exhibitorId).filter(Boolean));
  const tierAWithoutBriefs = tierAExhibitors.filter((exhibitor) => !briefedExhibitorIds.has(exhibitor.id));
  const tierAWithoutDrafts = tierAExhibitors.filter((exhibitor) => !hasRequiredEmailDrafts(drafts, exhibitor.id));
  const latestAgentRun = agentRuns[0] ?? null;
  const importTerminal = importStatus?.terminal || !importStatus?.hasImportJob;
  const searchedExhibitors = filterExhibitorsBySearch(exhibitors, exhibitorSearch);
  const visibleExhibitors = filterExhibitors(searchedExhibitors, scoreFilter);
  const sortedVisibleExhibitors = scoreSortDescending
    ? [...visibleExhibitors].sort((a, b) => {
        const scoreA = Number.isFinite(a.scoreTotal) ? a.scoreTotal : -1;
        const scoreB = Number.isFinite(b.scoreTotal) ? b.scoreTotal : -1;
        return scoreB - scoreA;
      })
    : visibleExhibitors;
  const exhibitorPageCount = Math.max(1, Math.ceil(sortedVisibleExhibitors.length / EXHIBITORS_PER_PAGE));
  const currentExhibitorPage = Math.min(exhibitorPage, exhibitorPageCount - 1);
  const pagedExhibitors = sortedVisibleExhibitors.slice(
    currentExhibitorPage * EXHIBITORS_PER_PAGE,
    (currentExhibitorPage + 1) * EXHIBITORS_PER_PAGE
  );
  const selectedExhibitor =
    exhibitors.find((exhibitor) => exhibitor.id === selectedExhibitorId) ?? null;
  const selectedBrief = briefs.find((brief) => brief.id === selectedBriefId) ?? null;
  const selectedBriefExhibitor =
    selectedBrief ? exhibitors.find((exhibitor) => exhibitor.id === selectedBrief.exhibitorId) ?? null : null;
  const selectedDraft = displayDrafts.find((draft) => draft.id === selectedDraftId) ?? null;
  const exhibitorSummary = [
    { label: "Total Exhibitors", value: totalExhibitors },
    { label: "Unique Exhibitors", value: uniqueExhibitors },
    { label: "Possible Duplicates", value: duplicateCount },
    { label: "Missing Website", value: missingWebsiteCount },
    { label: "Tier A", value: tierACount },
    { label: "Tier B", value: tierBCount },
    { label: "Needs Review", value: needsReviewCount },
    { label: "Excluded", value: excludedCount },
  ];
  const agentPlanItems = [
    {
      label: "Target exhibitor profile",
      detail: filledProfileFieldCount
        ? `${filledProfileFieldCount}/${COMMUNITY_PROFILE_FIELDS.length} criteria set`
        : "Add fit criteria before final prioritization",
      complete: filledProfileFieldCount === COMMUNITY_PROFILE_FIELDS.length,
      active: profileEditing || filledProfileFieldCount < COMMUNITY_PROFILE_FIELDS.length,
    },
    {
      label: "Exhibitor import",
      detail: totalExhibitors
        ? `${totalExhibitors} exhibitors available`
        : importStatus?.hasImportJob && !importTerminal
          ? "Import is running"
          : "Import or upload exhibitors",
      complete: totalExhibitors > 0,
      active: Boolean(importStatus?.hasImportJob && !importTerminal),
    },
    {
      label: "Profile-fit scoring",
      detail: scoredCount
        ? `${scoredCount}/${totalExhibitors} exhibitors scored`
        : totalExhibitors
          ? "Score exhibitors against the profile"
          : "Waiting for exhibitors",
      complete: totalExhibitors > 0 && scoredCount === totalExhibitors,
      active: totalExhibitors > 0 && scoredCount < totalExhibitors,
    },
    {
      label: "Tier A briefs",
      detail: tierACount
        ? `${briefs.filter((brief) => tierAExhibitors.some((exhibitor) => exhibitor.id === brief.exhibitorId)).length}/${tierACount} Tier A briefs generated`
        : "No Tier A exhibitors yet",
      complete: tierACount > 0 && tierAWithoutBriefs.length === 0,
      active: tierAWithoutBriefs.length > 0,
    },
    {
      label: "Email drafts",
      detail: tierACount
        ? `${tierAWithoutDrafts.length} Tier A exhibitors still need tailored drafts`
        : "Ready after Tier A briefs",
      complete: tierACount > 0 && tierAWithoutDrafts.length === 0,
      active: tierAWithoutDrafts.length > 0,
    },
  ];
  const agentRecommendations = [
    ...(tierAExhibitors.length
      ? [
          {
            title: "Prioritize Tier A outreach",
            detail: `${tierAExhibitors
              .slice(0, 3)
              .map((exhibitor) => exhibitor.companyName)
              .filter(Boolean)
              .join(", ")}${tierAExhibitors.length > 3 ? ` and ${tierAExhibitors.length - 3} more` : ""}`,
            action: "Review Tier A exhibitors",
          },
        ]
      : []),
    ...(tierAWithoutBriefs.length
      ? [
          {
            title: "Tier A briefs are queued",
            detail: `${tierAWithoutBriefs.length} Tier A exhibitors still need automatic briefs.`,
            action: "Watch Agent Runs",
          },
        ]
      : []),
    ...(tierAWithoutDrafts.length
      ? [
          {
            title: "Draft emails for best-fit companies",
            detail: `${tierAWithoutDrafts.length} Tier A exhibitors still need tailored email drafts.`,
            action: "Open Tier A exhibitor",
          },
        ]
      : []),
    ...(tierBExhibitors.length
      ? [
          {
            title: "Review strong Tier B prospects",
            detail: `${tierBExhibitors.length} Tier B exhibitors may move up with better data or refined criteria.`,
            action: "Filter Tier B",
          },
        ]
      : []),
    ...(missingWebsiteCount
      ? [
          {
            title: "Fill missing websites before outreach",
            detail: `${missingWebsiteCount} exhibitors are missing a website or domain.`,
            action: "Check exhibitors",
          },
        ]
      : []),
    ...(duplicateCount
      ? [
          {
            title: "Resolve possible duplicate exhibitors",
            detail: `${duplicateCount} records are marked as possible duplicates.`,
            action: "Filter duplicates",
          },
        ]
      : []),
  ].slice(0, 5);

  const handleExhibitorImport = async (preview) => {
    setUploadError("");
    try {
      await setWorkspaceExhibitorPreview(workspace.id, preview);
      setScoreFilter("All");
      setExhibitorPage(0);
      setUploadModalOpen(false);
      setUploadSuccessOpen(true);
    } catch {
      setUploadError("The exhibitor list could not be saved. Try uploading it again.");
    }
  };

  const handleScoreExhibitors = async () => {
    if (!targetProfileReady || scoringLoading) return;
    scoreExhibitorsControllerRef.current?.abort();
    const controller = new AbortController();
    scoreExhibitorsControllerRef.current = controller;

    setScoringLoading(true);
    setScoringError("");
    try {
      logTradeShowAgent("score exhibitors request started", { workspaceId: workspace.id });
      const response = await api.post(
        `/api/trade-show-agent/workspaces/${workspace.id}/exhibitors/enrich`,
        { maxExhibitors: Math.min(exhibitors.length, 100) },
        { signal: controller.signal, timeout: 30000 }
      );
      if (!mountedRef.current || controller.signal.aborted) return;
      const nextStatus = response.data?.status || null;
      setResearchStatus(nextStatus);
      if (nextStatus?.terminal) {
        setScoringLoading(false);
        await loadWorkspaces();
      }
      logTradeShowAgent("score exhibitors request completed", { workspaceId: workspace.id, status: response.status });
    } catch (error) {
      if (controller.signal.aborted) {
        logTradeShowAgent("score exhibitors request aborted", { workspaceId: workspace.id });
        return;
      }
      if (mountedRef.current) {
        setScoringError(error?.response?.data?.detail || error?.message || "Unable to score exhibitors.");
        setScoringLoading(false);
      }
      logTradeShowAgent("score exhibitors request failed", {
        workspaceId: workspace.id,
        status: error?.response?.status,
        message: error?.message,
      });
    } finally {
      if (scoreExhibitorsControllerRef.current === controller) {
        scoreExhibitorsControllerRef.current = null;
      }
    }
  };

  const handleEditProfile = () => {
    setProfileForm({
      ...EMPTY_COMMUNITY_PROFILE,
      ...(communityProfile ?? {}),
      targetIndustries: normalizeProfileArray(communityProfile?.targetIndustries),
      targetGeographies: normalizeProfileArray(communityProfile?.targetGeographies),
      preferredCompanySize: normalizeProfileArray(communityProfile?.preferredCompanySize),
      preferredGrowthSignals: normalizeProfileArray(communityProfile?.preferredGrowthSignals),
    });
    setProfileModalOpen(true);
    setProfileEditing(true);
  };

  const handleSaveProfile = () => {
    const profileFieldsToSave = { ...profileForm };
    delete profileFieldsToSave.excludedIndustries;
    delete profileFieldsToSave.keyValueProposition;
    delete profileFieldsToSave.outreachTone;
    const savedProfile = Object.fromEntries(
      Object.entries(profileFieldsToSave).map(([key, value]) => [
        key,
        ["targetIndustries", "targetGeographies", "preferredCompanySize", "preferredGrowthSignals"].includes(key)
          ? normalizeProfileArray(value)
          : cleanValue(value),
      ])
    );
    setWorkspaceCommunityProfile(workspace.id, savedProfile);
    setProfileEditing(false);
    setProfileModalOpen(false);
  };

  const handleCloseProfileModal = () => {
    setProfileModalOpen(false);
    setProfileEditing(false);
  };

  const handleWorkspaceTabChange = (_, value) => {
    if (value === "target-profile") {
      setProfileModalOpen(true);
      return;
    }
    setActiveTab(value);
  };

  const updateAgentRun = (runId, updates) => {
    setWorkspaceAgentRuns(workspace.id, (currentRuns) =>
      currentRuns.map((run) => (run.id === runId ? { ...run, ...updates } : run))
    );
  };

  const saveEmailDraftsForExhibitor = (exhibitor, brief) => {
    const generatedDrafts = [
      buildGeneralEmailDraft(workspace, communityProfile),
      buildTailoredEmailDraftForExhibitor(exhibitor, communityProfile, brief),
    ];
    setWorkspaceDrafts(workspace.id, (currentDrafts) =>
      mergeGeneratedEmailDrafts(currentDrafts, generatedDrafts, new Set([exhibitor.id]))
    );
    return generatedDrafts;
  };

  const handleGenerateBrief = async (exhibitor) => {
    if (!exhibitor || briefGenerationExhibitorId) return;
    generateBriefControllerRef.current?.abort();
    const controller = new AbortController();
    generateBriefControllerRef.current = controller;

    const runId = `run-brief-${exhibitor.id}-${Date.now()}`;
    const startedAt = new Date().toISOString();
    const companyName = exhibitor.companyName || "Unnamed Exhibitor";

    setBriefGenerationError("");
    setBriefGenerationExhibitorId(exhibitor.id);
    setWorkspaceAgentRuns(workspace.id, (currentRuns) => [
      {
        id: runId,
        type: "Company Brief",
        companyName,
        status: "Running",
        startedAt,
        completedAt: null,
        message: "Generating Brief...",
      },
      ...currentRuns,
    ]);

    try {
      logTradeShowAgent("generate brief request started", { workspaceId: workspace.id, exhibitorId: exhibitor.id });
      const response = await api.post("/api/trade-show-agent/generate-brief", {
        workspaceId: workspace.id,
        communityProfile: communityProfile ?? {},
        exhibitor,
      }, { signal: controller.signal, timeout: 90000 });
      if (!mountedRef.current || controller.signal.aborted) return;
      const generatedBrief = buildBriefFromResponse(exhibitor, response.data);

      setWorkspaceBriefs(workspace.id, (currentBriefs) => [
        generatedBrief,
        ...currentBriefs.filter((brief) => brief.exhibitorId !== exhibitor.id),
      ]);
      saveEmailDraftsForExhibitor(exhibitor, generatedBrief);
      updateAgentRun(runId, {
        status: "Completed",
        completedAt: new Date().toISOString(),
        message: "Brief and email drafts generated",
      });
      setSelectedExhibitorId(null);
      setSelectedBriefId(generatedBrief.id);
      setActiveTab(2);
      logTradeShowAgent("generate brief request completed", { workspaceId: workspace.id, exhibitorId: exhibitor.id, status: response.status });
    } catch (error) {
      if (controller.signal.aborted) {
        logTradeShowAgent("generate brief request aborted", { workspaceId: workspace.id, exhibitorId: exhibitor.id });
        return;
      }
      const timedOut = isRequestTimeout(error);
      const message = getBriefGenerationErrorMessage(error);
      updateAgentRun(runId, {
        status: timedOut ? "Delayed" : "Failed",
        completedAt: timedOut ? null : new Date().toISOString(),
        message,
      });
      setBriefGenerationError(message);
      logTradeShowAgent("generate brief request failed", {
        workspaceId: workspace.id,
        exhibitorId: exhibitor.id,
        status: error?.response?.status,
        message: error?.message,
      });
    } finally {
      if (generateBriefControllerRef.current === controller) {
        generateBriefControllerRef.current = null;
      }
      if (mountedRef.current && !controller.signal.aborted) {
        setBriefGenerationExhibitorId(null);
      }
    }
  };

  const handleGenerateDraft = (exhibitor) => {
    const existingBrief = briefs.find((brief) => brief.exhibitorId === exhibitor.id);
    const generatedDrafts = saveEmailDraftsForExhibitor(exhibitor, existingBrief);
    const tailoredDraft = generatedDrafts.find((draft) => draft.draftType === DRAFT_TYPE_TAILORED) || generatedDrafts[0];
    setSelectedExhibitorId(null);
    setSelectedDraftId(tailoredDraft.id);
    setActiveTab(3);
  };

  const handleDirectoryPreview = async () => {
    directoryPreviewControllerRef.current?.abort();
    const controller = new AbortController();
    directoryPreviewControllerRef.current = controller;

    setDirectoryPreviewLoading(true);
    setDirectoryImportError("");
    setImportSummary(null);
    try {
      logTradeShowAgent("directory preview request started", { workspaceId: workspace.id });
      const response = await api.post(
        `/api/trade-show-agent/workspaces/${workspace.id}/exhibitors/preview`,
        { directoryUrl: directoryUrl.trim() || null },
        { signal: controller.signal, timeout: 120000 }
      );
      if (!mountedRef.current || controller.signal.aborted) return;
      setDirectoryUrl(response.data.directoryUrl || directoryUrl);
      setScoreFilter("All");
      setExhibitorPage(0);
      await loadWorkspaces();
      logTradeShowAgent("directory preview request completed", { workspaceId: workspace.id, status: response.status });
    } catch (error) {
      if (controller.signal.aborted) {
        logTradeShowAgent("directory preview request aborted", { workspaceId: workspace.id });
        return;
      }
      setDirectoryImportError(getDirectoryImportErrorMessage(error));
      logTradeShowAgent("directory preview request failed", {
        workspaceId: workspace.id,
        status: error?.response?.status,
        message: error?.message,
      });
    } finally {
      if (directoryPreviewControllerRef.current === controller) {
        directoryPreviewControllerRef.current = null;
      }
      if (mountedRef.current && !controller.signal.aborted) {
        setDirectoryPreviewLoading(false);
      }
    }
  };

  const renderProfileField = (field) => {
    const FieldIcon = field.icon;
    const fieldInputSx = {
      "& .MuiOutlinedInput-root": {
        borderRadius: 2,
        backgroundColor: "#ffffff",
        "&.Mui-focused fieldset": { borderColor: field.accent },
      },
      "& .MuiSelect-select": {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 0.5,
        minHeight: "24px",
        whiteSpace: "normal",
      },
      "& .MuiInputLabel-root.Mui-focused": { color: field.accent },
      "& .MuiFormHelperText-root": { color: BRAND_GREEN, fontWeight: 700 },
    };

    if (field.type === "geography") {
      const selected = normalizeProfileArray(profileForm[field.key]);
      const options = [...new Set([...TARGET_GEOGRAPHY_OPTIONS, ...selected])];
      return (
        <Autocomplete
          key={field.key}
          multiple
          size="small"
          disableCloseOnSelect
          disableClearable
          filterSelectedOptions
          options={options}
          groupBy={(option) => TARGET_GEOGRAPHY_GROUP_BY_OPTION.get(option) || "Other"}
          slotProps={{
            paper: {
              sx: {
                "& .MuiAutocomplete-option, & .MuiAutocomplete-groupLabel": {
                  color: BRAND_BLUE,
                  fontWeight: 700,
                },
              },
            },
          }}
          value={selected}
          onChange={(_event, values) =>
            setProfileForm((prev) => ({ ...prev, [field.key]: values }))
          }
          renderTags={(values) => (
            <Chip
              label={values.length > 1 ? `${values[0]} +${values.length - 1}` : values[0]}
              size="small"
              sx={{ ...PROFILE_SELECTED_CHIP_SX, maxWidth: "min(100%, 260px)" }}
            />
          )}
          renderOption={(props, option, { selected: isSelected }) => (
            <li {...props} key={option}>
              <Checkbox
                size="small"
                checked={isSelected}
                icon={<RadioButtonUncheckedIcon fontSize="small" />}
                checkedIcon={<CheckCircleIcon fontSize="small" />}
                sx={{ mr: 1, color: BRAND_BLUE, "&.Mui-checked": { color: BRAND_BLUE } }}
              />
              {option}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label={field.label}
              helperText={field.helperText}
              placeholder={selected.length ? "" : "Search countries, states, or regions"}
              size="small"
              fullWidth
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <FieldIcon sx={{ fontSize: 18, color: field.accent }} />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
              sx={fieldInputSx}
            />
          )}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
              paddingTop: "8.5px",
              paddingBottom: "8.5px",
              paddingLeft: "14px",
            },
            "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
              padding: "0 !important",
              color: BRAND_BLUE,
            },
            ...(selected.length ? {
              "&:not(.Mui-focused) .MuiAutocomplete-input": {
                minWidth: 0,
                width: 0,
                flexGrow: 0,
              },
              "&.Mui-focused .MuiAutocomplete-input": { marginLeft: 0.5 },
            } : {}),
          }}
        />
      );
    }

    if (["sector", "select"].includes(field.type)) {
      const fieldValue = normalizeProfileArray(profileForm[field.key]);
      const fieldOptions = field.type === "sector" ? sectorOptions : field.options;
      const customOptions = fieldValue.filter((value) => !fieldOptions.includes(value));
      const formatOption = field.formatOption || (field.type === "sector" ? formatBroadSectorLabel : (value) => value);
      const profileMenuItemSx = { color: BRAND_BLUE, fontWeight: 700 };
      const profileCheckboxProps = {
        size: "small",
        icon: <RadioButtonUncheckedIcon fontSize="small" />,
        checkedIcon: <CheckCircleIcon fontSize="small" />,
        sx: {
          color: BRAND_BLUE,
          "&.Mui-checked": { color: BRAND_BLUE },
        },
      };
      const renderSelected = (selected) => {
        const selectedValues = normalizeProfileArray(selected);
        if (!selectedValues.length) {
          return <Box component="span" sx={{ color: "#b8c2d1" }}>Not set</Box>;
        }
        const firstValue = selectedValues[0];
        const label =
          selectedValues.length > 1
            ? `${formatOption(firstValue)} +${selectedValues.length - 1}`
            : formatOption(firstValue);
        return (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
            <Chip
              key={firstValue}
              label={label}
              size="small"
              sx={{
                ...PROFILE_SELECTED_CHIP_SX,
                maxWidth: "min(100%, 260px)",
                flexShrink: 0,
              }}
            />
          </Box>
        );
      };

      return (
        <TextField
          key={field.key}
          select
          label={field.label}
          helperText={field.helperText}
          value={fieldValue}
          onChange={(event) =>
            setProfileForm((prev) => ({ ...prev, [field.key]: normalizeProfileArray(event.target.value) }))
          }
          size="small"
          fullWidth
          SelectProps={{
            multiple: true,
            displayEmpty: true,
            renderValue: renderSelected,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FieldIcon sx={{ fontSize: 18, color: field.accent }} />
              </InputAdornment>
            ),
          }}
          sx={fieldInputSx}
        >
          {customOptions.map((option) => (
            <MenuItem key={`${field.key}-custom-${option}`} value={option} sx={profileMenuItemSx}>
              <Checkbox {...profileCheckboxProps} checked />
              {formatOption(option)}
            </MenuItem>
          ))}
          {fieldOptions.map((option) => (
            <MenuItem key={option} value={option} sx={profileMenuItemSx}>
              <Checkbox {...profileCheckboxProps} checked={fieldValue.includes(option)} />
              {formatOption(option)}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    return (
      <TextField
        key={field.key}
        label={field.label}
        helperText={field.helperText}
        value={profileForm[field.key]}
        onChange={(event) =>
          setProfileForm((prev) => ({ ...prev, [field.key]: event.target.value }))
        }
        multiline
        minRows={3}
        size="small"
        fullWidth
        sx={fieldInputSx}
      />
    );
  };

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", backgroundColor: PAGE_BG, pb: 6 }}>
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          borderBottom: "1px solid #dce4ef",
          background: "#ffffff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: { xs: 2, md: 3 },
            py: 1.75,
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              background: BRAND_BLUE,
              color: "#ffffff",
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <SmartToyIcon sx={{ fontSize: 19 }} />
          </Box>
          <Typography
            sx={{
              fontSize: "1.05rem",
              fontWeight: 800,
              color: BRAND_GREEN,
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
            }}
          >
            Trade Show Agent
          </Typography>
          <Typography sx={{ color: "#c5d0de", fontSize: "1rem", mx: 0.5 }}>/</Typography>
          <Typography
            sx={{
              fontSize: "0.95rem",
              fontWeight: 600,
              color: "#5a6a80",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {workspace.workspaceName}
          </Typography>
        </Box>
      </Box>

      {/* Page Content */}
      <Box sx={{ px: { xs: 2, md: 3 }, mt: 3 }}>
        {/* Back link */}
        <Button
          startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate("/trade-show-agent")}
          size="small"
          sx={{
            textTransform: "none",
            color: "#7a8fa8",
            fontWeight: 500,
            fontSize: "0.82rem",
            mb: 2,
            px: 0,
            "&:hover": { color: BRAND_BLUE, background: "transparent" },
          }}
          disableRipple
        >
          All Workspaces
        </Button>

        {/* Workspace info */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap", mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 800, color: BRAND_BLUE, fontSize: "1.5rem" }}
              >
                {workspace.workspaceName}
              </Typography>
              <Chip
                label={workspace.status}
                size="small"
                sx={{
                  backgroundColor: chipStyle.bg,
                  color: chipStyle.color,
                  fontWeight: 700,
                  fontSize: 11,
                  height: 22,
                }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2.5, mt: 0.75, flexWrap: "wrap" }}>
              {workspace.tradeShowName && (
                <Typography sx={WORKSPACE_META_TEXT_SX}>
                  <Box component="span" sx={WORKSPACE_META_LABEL_SX}>Trade Show:</Box>{" "}
                  {workspace.tradeShowName}
                </Typography>
              )}
              {workspace.industry && (
                <Typography sx={WORKSPACE_META_TEXT_SX}>
                  <Box component="span" sx={WORKSPACE_META_LABEL_SX}>Industry:</Box>{" "}
                  {workspace.industry}
                </Typography>
              )}
              {(workspace.city || workspace.state || workspace.country) && workspace.location === [workspace.city, workspace.state, workspace.country].filter(Boolean).join(", ") ? (
                <>
                  {workspace.city && (
                    <Typography sx={WORKSPACE_META_TEXT_SX}>
                      <Box component="span" sx={WORKSPACE_META_LABEL_SX}>City:</Box>{" "}
                      {workspace.city}
                    </Typography>
                  )}
                  {workspace.state && (
                    <Typography sx={WORKSPACE_META_TEXT_SX}>
                      <Box component="span" sx={WORKSPACE_META_LABEL_SX}>State / Province:</Box>{" "}
                      {workspace.state}
                    </Typography>
                  )}
                  {workspace.country && (
                    <Typography sx={WORKSPACE_META_TEXT_SX}>
                      <Box component="span" sx={WORKSPACE_META_LABEL_SX}>Country:</Box>{" "}
                      {workspace.country}
                    </Typography>
                  )}
                </>
              ) : workspace.location && (
                <Typography sx={WORKSPACE_META_TEXT_SX}>
                  <Box component="span" sx={WORKSPACE_META_LABEL_SX}>Location:</Box>{" "}
                  {workspace.location}
                </Typography>
              )}
              {workspace.month && (
                <Typography sx={WORKSPACE_META_TEXT_SX}>
                  <Box component="span" sx={WORKSPACE_META_LABEL_SX}>Month:</Box>{" "}
                  {workspace.month}
                </Typography>
              )}
              {workspace.year && (
                <Typography sx={WORKSPACE_META_TEXT_SX}>
                  <Box component="span" sx={WORKSPACE_META_LABEL_SX}>Year:</Box>{" "}
                  {workspace.year}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "rgba(0,51,108,0.1)", mb: 0 }} />

        <Box sx={{ mt: 2 }}>
          <AutoImportStatusPanel status={importStatus} onManualUpload={() => setUploadModalOpen(true)} />
        </Box>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          variant="scrollable"
          scrollButtons="auto"
          onChange={handleWorkspaceTabChange}
          sx={{
            borderBottom: "1px solid #e0e8f0",
            mb: 3,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.875rem",
              color: "#7a8fa8",
              minHeight: 44,
              px: 2,
            },
            "& .Mui-selected": { color: `${BRAND_BLUE} !important` },
            "& .MuiTabs-indicator": { backgroundColor: BRAND_BLUE },
          }}
        >
          <Tab label={TABS[0]} value={0} disableRipple />
          <Tab label="Target Exhibitor Profile" value="target-profile" disableRipple />
          {TABS.slice(1).flatMap((tab, index) => [
            <Tab key={tab} label={tab} value={index + 1} disableRipple />,
            ...(TRADE_SHOW_ATTENDEES_ENABLED && index === 0 ? [<Tab key="attendees" label="Attendees" value="attendees" disableRipple />] : []),
          ])}
        </Tabs>

        {/* Tab content */}
        {TRADE_SHOW_ATTENDEES_ENABLED && activeTab === "attendees" ? (
          <WorkspaceAttendees key={workspace.id} workspaceId={workspace.id} onViewExhibitor={(exhibitorId) => {
            setActiveTab(1);
            setSelectedExhibitorId(exhibitorId);
          }} />
        ) : activeTab === 0 ? (
          <Box sx={{ display: "grid", gap: 2.5 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.05fr 1fr" }, gap: 2.5 }}>
              <Box sx={{ ...PANEL_SX, overflow: "hidden" }}>
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    borderBottom: "1px solid #edf1f6",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: BRAND_BLUE }}>
                      Agent Plan
                    </Typography>
                    <Typography sx={{ fontSize: "0.82rem", color: "#7a8fa8", mt: 0.25 }}>
                      Current workflow state for this trade show.
                    </Typography>
                  </Box>
                  {latestAgentRun && (
                    <Chip
                      label={latestAgentRun.status}
                      size="small"
                      sx={{
                        height: 24,
                        fontWeight: 800,
                        ...getAgentRunChipSx(latestAgentRun.status),
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ p: 2.5, display: "grid", gap: 1.35 }}>
                  {agentPlanItems.map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "28px minmax(0, 1fr)",
                        gap: 1.25,
                        alignItems: "flex-start",
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          display: "grid",
                          placeItems: "center",
                          backgroundColor: item.complete
                            ? "rgba(100,131,21,0.12)"
                            : item.active
                              ? "rgba(0,51,108,0.1)"
                              : "rgba(122,143,168,0.14)",
                          color: item.complete ? BRAND_GREEN : item.active ? BRAND_BLUE : "#7a8fa8",
                        }}
                      >
                        {item.complete ? (
                          <CheckCircleIcon sx={{ fontSize: 16 }} />
                        ) : item.active ? (
                          <SmartToyIcon sx={{ fontSize: 15 }} />
                        ) : (
                          <RadioButtonUncheckedIcon sx={{ fontSize: 16 }} />
                        )}
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.88rem", fontWeight: 800, color: BRAND_BLUE }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ fontSize: "0.78rem", color: "#7a8fa8", mt: 0.15 }}>
                          {item.detail}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box sx={{ ...PANEL_SX, overflow: "hidden" }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #edf1f6" }}>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: BRAND_BLUE }}>
                    Agent Recommendations
                  </Typography>
                  <Typography sx={{ fontSize: "0.82rem", color: "#7a8fa8", mt: 0.25 }}>
                    Suggested next moves based on the current exhibitor set.
                  </Typography>
                </Box>
                <Box sx={{ p: 2.5, display: "grid", gap: 1.25 }}>
                  {agentRecommendations.length ? (
                    agentRecommendations.map((item) => (
                      <Box
                        key={item.title}
                        sx={{
                          border: "1px solid #edf1f6",
                          borderRadius: 2,
                          p: 1.5,
                          backgroundColor: "#fbfcfe",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                          <Typography sx={{ fontSize: "0.88rem", fontWeight: 800, color: BRAND_BLUE }}>
                            {item.title}
                          </Typography>
                          <Chip
                            label={item.action}
                            size="small"
                            sx={{
                              height: 22,
                              borderRadius: 1.25,
                              backgroundColor: "rgba(100,131,21,0.12)",
                              color: BRAND_GREEN,
                              fontWeight: 800,
                              fontSize: 11,
                            }}
                          />
                        </Box>
                        <Typography sx={{ fontSize: "0.8rem", color: "#5a6a80", mt: 0.6, lineHeight: 1.45 }}>
                          {item.detail}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Box
                      sx={{
                        border: "1.5px dashed #c5d0de",
                        borderRadius: 2,
                        py: 4,
                        px: 2,
                        textAlign: "center",
                        backgroundColor: "#fbfcfe",
                      }}
                    >
                      <SmartToyIcon sx={{ fontSize: 34, color: "#c5d0de", mb: 1 }} />
                      <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: BRAND_BLUE }}>
                        No recommendations yet.
                      </Typography>
                      <Typography sx={{ fontSize: "0.8rem", color: "#7a8fa8", mt: 0.4 }}>
                        Add a target profile and import exhibitors to activate recommendations.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ) : activeTab === 1 ? (
          <Box>
            <Box
              sx={{
                border: "1px solid #dce4ef",
                borderRadius: 2,
                backgroundColor: "#ffffff",
                p: 2,
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <TextField
                  label="Exhibitor Directory URL"
                  placeholder="https://..."
                  value={directoryUrl}
                  onChange={(event) => setDirectoryUrl(event.target.value)}
                  size="small"
                  sx={{ minWidth: { xs: "100%", md: 420 }, flex: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PublicIcon sx={{ fontSize: 18, color: BRAND_BLUE }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={directoryPreviewLoading ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadIcon />}
                  onClick={handleDirectoryPreview}
                  disabled={directoryPreviewLoading}
                  sx={PRIMARY_BUTTON_SX}
                >
                  Import Exhibitors
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleDirectoryPreview}
                  disabled={directoryPreviewLoading}
                  sx={{ textTransform: "none", fontWeight: 700, color: BRAND_BLUE, borderColor: BRAND_BLUE }}
                >
                  Refresh
                </Button>
              </Box>
              {directoryImportError && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  {directoryImportError}
                </Alert>
              )}
              {importSummary && (
                <Alert severity="success" sx={{ mt: 1.5 }}>
                  {`Selected: ${importSummary.selected || 0} | Added: ${importSummary.added || 0} | Linked Existing: ${
                    importSummary.linked_existing || 0
                  } | Skipped Duplicates: ${importSummary.skipped_duplicates || 0} | Failed: ${
                    importSummary.failed || 0
                  }`}
                </Alert>
              )}
            </Box>
            {exhibitors.length ? (
              <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: BRAND_BLUE }}>
                    Exhibitor Preview
                  </Typography>
                  <Typography sx={{ fontSize: "0.82rem", color: "#7a8fa8", mt: 0.25 }}>
                    {exhibitors.length} exhibitors imported from {exhibitorPreview.fileName}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Tooltip
                    title={!targetProfileReady ? "Complete Target Exhibitor Profile before scoring." : ""}
                    arrow
                    disableHoverListener={targetProfileReady}
                  >
                    <span>
                      <Button
                        variant="contained"
                        startIcon={<AssessmentIcon />}
                        onClick={handleScoreExhibitors}
                        disabled={!targetProfileReady || !exhibitors.length || scoringLoading}
                        sx={{
                          ...PRIMARY_BUTTON_SX,
                          backgroundColor: "#0F766E",
                          "&:hover": { backgroundColor: "#115E59" },
                          "&.Mui-disabled": {
                            backgroundColor: "#d8e0ea",
                            color: "#7a8fa8",
                          },
                        }}
                      >
                        {scoringLoading ? "Researching Signals..." : "Score Exhibitors"}
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    variant="contained"
                    startIcon={<UploadFileIcon />}
                    onClick={() => setUploadModalOpen(true)}
                    sx={{
                      ...PRIMARY_BUTTON_SX,
                      backgroundColor: BRAND_BLUE,
                      "&:hover": { backgroundColor: "#002a59" },
                    }}
                  >
                    Upload Exhibitor List
                  </Button>
                </Box>
              </Box>
              {scoringError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {scoringError}
                </Alert>
              )}
              {researchStatus?.hasResearchJob && !researchStatus.terminal && (
                <Box
                  sx={{
                    border: "1px solid #dce4ef",
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                    backgroundColor: "#ffffff",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 1 }}>
                    <Box>
                      <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: BRAND_BLUE }}>
                        Researching Growth Signals
                      </Typography>
                      <Typography sx={{ fontSize: "0.78rem", color: "#7a8fa8", mt: 0.25 }}>
                        {researchStatus.processedExhibitors || 0} / {researchStatus.totalExhibitors || exhibitors.length} exhibitors researched
                      </Typography>
                    </Box>
                    <Chip
                      label={formatResearchStage(researchStatus.currentStage || researchStatus.status)}
                      size="small"
                      sx={{
                        height: 24,
                        fontWeight: 800,
                        color: BRAND_BLUE,
                        backgroundColor: "rgba(0,51,108,0.08)",
                      }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.max(0, Math.min(100, Number(researchStatus.progressPercent || 0)))}
                    sx={{
                      height: 7,
                      borderRadius: 999,
                      backgroundColor: "#edf1f6",
                      "& .MuiLinearProgress-bar": { backgroundColor: BRAND_GREEN },
                    }}
                  />
                  <Typography sx={{ fontSize: "0.75rem", color: "#7a8fa8", mt: 0.75, fontWeight: 700 }}>
                    {researchStatus.matchedSignals || 0} exhibitors with verified selected signals
                    {researchStatus.failedExhibitors ? `, ${researchStatus.failedExhibitors} failed` : ""}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                {exhibitorSummary.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      border: "1px solid #dce4ef",
                      borderRadius: 2,
                      backgroundColor: "#ffffff",
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography sx={{ fontSize: "0.74rem", fontWeight: 800, color: BRAND_GREEN, mb: 0.5 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: BRAND_BLUE }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search exhibitors"
                  value={exhibitorSearch}
                  onChange={(event) => {
                    setExhibitorSearch(event.target.value);
                    setExhibitorPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: "#7a8fa8" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: { xs: "1 1 100%", sm: "0 1 640px" },
                    width: { xs: "100%", sm: 640 },
                    minWidth: 0,
                    mr: 1,
                  }}
                />
                {SCORE_FILTERS.map((filter) => (
                  <Chip
                    key={filter}
                    label={filter}
                    clickable
                    onClick={() => {
                      setScoreFilter(filter);
                      setExhibitorPage(0);
                    }}
                    sx={{
                      height: 30,
                      borderRadius: 1.5,
                      fontWeight: 800,
                      backgroundColor: scoreFilter === filter ? BRAND_BLUE : "#ffffff",
                      border: "1px solid #dce4ef",
                      color: scoreFilter === filter ? "#ffffff" : "#3a4a5c",
                      "&:hover": {
                        backgroundColor: scoreFilter === filter ? BRAND_BLUE : "rgba(0,51,108,0.06)",
                      },
                    }}
                  />
                ))}
              </Box>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: "1px solid #dce4ef",
                  borderRadius: 2,
                  overflow: "auto",
                  maxHeight: 560,
                }}
              >
                <Table stickyHeader size="small" aria-label="Imported exhibitor preview rows">
                  <TableHead>
                    <TableRow>
                      {EXHIBITOR_COLUMNS.map((column) => (
                        <TableCell
                          key={column.key}
                          sx={{
                            fontWeight: 800,
                            color: BRAND_BLUE,
                            backgroundColor: "#f7f9fc",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {column.key === "scoreTotal" ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              {column.label}
                              <IconButton
                                size="small"
                                aria-label="Sort scores highest to lowest"
                                onClick={() => {
                                  setScoreSortDescending(true);
                                  setExhibitorPage(0);
                                }}
                                sx={{
                                  width: 26,
                                  height: 26,
                                  color: scoreSortDescending ? BRAND_GREEN : BRAND_BLUE,
                                }}
                              >
                                <ArrowDownwardIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </Box>
                          ) : (
                            column.label
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedExhibitors.map((exhibitor) => (
                      <TableRow
                        key={exhibitor.id}
                        hover
                        tabIndex={0}
                        onClick={() => setSelectedExhibitorId(exhibitor.id)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            setSelectedExhibitorId(exhibitor.id);
                          }
                        }}
                        sx={{ cursor: "pointer" }}
                      >
                        {EXHIBITOR_COLUMNS.map((column) => (
                          <TableCell
                            key={`${exhibitor.id}-${column.key}`}
                            sx={EXHIBITOR_TABLE_CELL_SX}
                          >
                            {column.key === "duplicateStatus" ? (
                              <Chip
                                label={
                                  exhibitor.isDuplicate
                                    ? `Possible duplicate of ${exhibitor.duplicateOfId}`
                                    : "Unique"
                                }
                                size="small"
                                sx={{
                                  height: 22,
                                  backgroundColor: exhibitor.isDuplicate
                                    ? "rgba(183,121,31,0.14)"
                                    : "rgba(100,131,21,0.12)",
                                  color: exhibitor.isDuplicate ? "#B7791F" : BRAND_GREEN,
                                  fontWeight: 700,
                                  fontSize: 11,
                                }}
                              />
                            ) : column.key === "scoreTotal" ? (
                              <Typography sx={{ fontWeight: 800, color: BRAND_BLUE, fontSize: "0.86rem" }}>
                                {Number.isFinite(exhibitor.scoreTotal) ? exhibitor.scoreTotal : "-"}
                              </Typography>
                            ) : column.key === "tier" ? (
                              <Chip
                                label={exhibitor.tier || "Unscored"}
                                size="small"
                                sx={{
                                  height: 22,
                                  backgroundColor:
                                    exhibitor.tier === "Excluded"
                                      ? "rgba(211,47,47,0.12)"
                                      : exhibitor.tier === "Tier A"
                                      ? "rgba(100,131,21,0.12)"
                                      : exhibitor.tier === "Tier B"
                                        ? "rgba(0,51,108,0.1)"
                                        : exhibitor.tier === "Tier C"
                                          ? "rgba(183,121,31,0.14)"
                                          : "rgba(122,143,168,0.14)",
                                  color:
                                    exhibitor.tier === "Excluded"
                                      ? "#D32F2F"
                                      : exhibitor.tier === "Tier A"
                                      ? BRAND_GREEN
                                      : exhibitor.tier === "Tier B"
                                        ? BRAND_BLUE
                                        : exhibitor.tier === "Tier C"
                                          ? "#B7791F"
                                          : "#5a6a80",
                                  fontWeight: 800,
                                  fontSize: 11,
                                }}
                              />
                            ) : column.key === "matchStatus" ? (
                              <Chip
                                label={exhibitor.matchStatus || (exhibitor.importedCompanyId ? "Imported" : "Not imported")}
                                size="small"
                                sx={{
                                  height: 22,
                                  backgroundColor: exhibitor.importedCompanyId
                                    ? "rgba(100,131,21,0.12)"
                                    : "rgba(122,143,168,0.14)",
                                  color: exhibitor.importedCompanyId ? BRAND_GREEN : "#5a6a80",
                                  fontWeight: 800,
                                  fontSize: 11,
                                }}
                              />
                            ) : (
                              <Box sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                {exhibitor[column.key] || "-"}
                              </Box>
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {!visibleExhibitors.length && (
                      <TableRow>
                        <TableCell colSpan={EXHIBITOR_COLUMNS.length} align="center" sx={{ py: 4 }}>
                          <Typography sx={{ color: "#7a8fa8", fontWeight: 600 }}>
                            No exhibitors match this filter.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={visibleExhibitors.length}
                page={currentExhibitorPage}
                onPageChange={(_, page) => setExhibitorPage(page)}
                rowsPerPage={EXHIBITORS_PER_PAGE}
                onRowsPerPageChange={() => undefined}
                rowsPerPageOptions={[EXHIBITORS_PER_PAGE]}
                sx={{ color: BRAND_BLUE }}
              />
              </Box>
            ) : directoryPreviewLoading ? (
              <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 8,
                gap: 2,
              }}
            >
              <CircularProgress size={32} />
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, color: BRAND_BLUE }}>
                Loading exhibitor directory...
              </Typography>
              <Typography sx={{ fontSize: "0.82rem", color: "#7a8fa8", textAlign: "center", maxWidth: 420 }}>
                This can take a moment while the provider directory is fetched and normalized.
              </Typography>
              </Box>
            ) : (
              <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 8,
                gap: 2,
              }}
            >
              <UploadFileIcon sx={{ fontSize: 48, color: "#c5d0de" }} />
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 500, color: "#8a9ab0" }}>
                No exhibitors imported yet.
              </Typography>
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={() => setUploadModalOpen(true)}
                sx={PRIMARY_BUTTON_SX}
              >
                Upload Exhibitor List
              </Button>
              </Box>
            )}
          </Box>
        ) : activeTab === 2 ? (
          <Box>
            {briefs.length ? (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: "1px solid #dce4ef",
                  borderRadius: 2,
                  overflow: "auto",
                }}
              >
                <Table size="small" aria-label="Generated exhibitor briefs">
                  <TableHead>
                    <TableRow>
                      {["Company Name", "Tier", "Score", "Brief Status", "Generated At", ""].map((header) => (
                        <TableCell
                          key={header}
                          sx={{
                            fontWeight: 800,
                            color: BRAND_BLUE,
                            backgroundColor: "#f7f9fc",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {briefs.map((brief) => (
                      <TableRow key={brief.id} hover>
                        <TableCell sx={{ fontWeight: 700, color: BRAND_BLUE }}>
                          {brief.companyName}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={brief.tier}
                            size="small"
                            sx={{
                              height: 22,
                              backgroundColor: "rgba(0,51,108,0.08)",
                              color: BRAND_BLUE,
                              fontWeight: 800,
                              fontSize: 11,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: BRAND_BLUE, fontWeight: 700 }}>
                          {Number.isFinite(brief.score) ? brief.score : "-"}
                        </TableCell>
                        <TableCell sx={{ color: BRAND_BLUE }}>{brief.status}</TableCell>
                        <TableCell sx={{ color: BRAND_BLUE }}>{formatDateTime(brief.generatedAt)}</TableCell>
                        <TableCell align="right">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setSelectedBriefId(brief.id)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 800,
                              color: BRAND_BLUE,
                              borderColor: BRAND_BLUE,
                            }}
                          >
                            View Brief
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                sx={{
                  border: "1.5px dashed #c5d0de",
                  borderRadius: 2,
                  py: 7,
                  px: 2,
                  textAlign: "center",
                  backgroundColor: "#ffffff",
                }}
              >
                <DescriptionIcon sx={{ fontSize: 42, color: "#c5d0de", mb: 1 }} />
                <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: BRAND_BLUE, mb: 0.75 }}>
                  No briefs generated yet.
                </Typography>
                <Typography sx={{ fontSize: "0.84rem", color: "#7a8fa8" }}>
                  Open an exhibitor from the Exhibitors tab and generate an AI company brief.
                </Typography>
              </Box>
            )}
          </Box>
        ) : activeTab === 3 ? (
          <Box>
            {displayDrafts.length ? (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: "1px solid #dce4ef",
                  borderRadius: 2,
                  overflow: "auto",
                }}
              >
                <Table size="small" aria-label="Generated outreach drafts">
                  <TableHead>
                    <TableRow>
                      {["Company Name", "Draft Type", "Status", "Generated At", ""].map((header) => (
                        <TableCell
                          key={header}
                          sx={{
                            fontWeight: 800,
                            color: BRAND_BLUE,
                            backgroundColor: "#f7f9fc",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayDrafts.map((draft) => (
                      <TableRow key={draft.id} hover>
                        <TableCell sx={{ fontWeight: 700, color: BRAND_BLUE }}>
                          {draft.companyName}
                        </TableCell>
                        <TableCell sx={{ color: BRAND_BLUE }}>{draft.draftType}</TableCell>
                        <TableCell>
                          <Chip
                            label={draft.status}
                            size="small"
                            sx={{
                              height: 22,
                              backgroundColor: "rgba(0,51,108,0.08)",
                              color: BRAND_BLUE,
                              fontWeight: 800,
                              fontSize: 11,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: BRAND_BLUE }}>{formatDateTime(draft.generatedAt)}</TableCell>
                        <TableCell align="right">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setSelectedDraftId(draft.id)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 800,
                              color: BRAND_BLUE,
                              borderColor: BRAND_BLUE,
                            }}
                          >
                            View Draft
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                sx={{
                  border: "1.5px dashed #c5d0de",
                  borderRadius: 2,
                  py: 7,
                  px: 2,
                  textAlign: "center",
                  backgroundColor: "#ffffff",
                }}
              >
                <EmailIcon sx={{ fontSize: 42, color: "#c5d0de", mb: 1 }} />
                <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: BRAND_BLUE, mb: 0.75 }}>
                  No email drafts generated yet.
                </Typography>
                <Typography sx={{ fontSize: "0.84rem", color: "#7a8fa8" }}>
                  One general email and tailored company emails will be generated after briefs are created.
                </Typography>
              </Box>
            )}
          </Box>
        ) : activeTab === 5 ? (
          <Box>
            {agentRuns.length ? (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: "1px solid #dce4ef",
                  borderRadius: 2,
                  overflow: "auto",
                }}
              >
                <Table size="small" aria-label="Trade Show Agent run history">
                  <TableHead>
                    <TableRow>
                      {["Run Type", "Company", "Status", "Started", "Completed", "Message"].map((header) => (
                        <TableCell
                          key={header}
                          sx={{
                            fontWeight: 800,
                            color: BRAND_BLUE,
                            backgroundColor: "#f7f9fc",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {agentRuns.map((run) => (
                      <TableRow key={run.id} hover>
                        <TableCell sx={{ fontWeight: 700, color: "#3a4a5c" }}>
                          {run.type}
                        </TableCell>
                        <TableCell sx={{ color: "#3a4a5c" }}>{run.companyName}</TableCell>
                        <TableCell>
                          <Chip
                            label={run.status}
                            size="small"
                            sx={{
                              height: 22,
                              ...getAgentRunChipSx(run.status),
                              fontWeight: 800,
                              fontSize: 11,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: "#3a4a5c" }}>{formatDateTime(run.startedAt)}</TableCell>
                        <TableCell sx={{ color: "#3a4a5c" }}>{formatDateTime(run.completedAt)}</TableCell>
                        <TableCell sx={{ color: "#3a4a5c", minWidth: 220 }}>{run.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                sx={{
                  border: "1.5px dashed #c5d0de",
                  borderRadius: 2,
                  py: 7,
                  px: 2,
                  textAlign: "center",
                  backgroundColor: "#ffffff",
                }}
              >
                <SmartToyIcon sx={{ fontSize: 42, color: "#c5d0de", mb: 1 }} />
                <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: BRAND_BLUE, mb: 0.75 }}>
                  No agent runs yet.
                </Typography>
                <Typography sx={{ fontSize: "0.84rem", color: "#7a8fa8" }}>
                  Generate a company brief to create the first run-history entry.
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 8,
              gap: 1.5,
            }}
          >
            <SmartToyIcon sx={{ fontSize: 48, opacity: 0.2, color: "#8a9ab0" }} />
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 500, color: "#8a9ab0" }}>
              {TABS[activeTab]} — coming soon
            </Typography>
          </Box>
        )}
      </Box>

      <Dialog
        open={profileModalOpen}
        onClose={handleCloseProfileModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: "1px solid #dce4ef",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            borderBottom: "1px solid #e8edf4",
            px: 3,
            py: 2,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              background: BRAND_BLUE,
              color: "#ffffff",
            }}
          >
            <HubIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: BRAND_BLUE }}>
              Target Exhibitor Profile
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "#7a8fa8", mt: 0.35 }}>
              Define the fit signals used to score and prioritize exhibitors.
            </Typography>
          </Box>
          <IconButton onClick={handleCloseProfileModal} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          {profileEditing ? (
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 1.5,
                    display: "grid",
                    placeItems: "center",
                    backgroundColor: "rgba(0,51,108,0.08)",
                    color: BRAND_BLUE,
                    flexShrink: 0,
                  }}
                >
                  <FactoryIcon sx={{ fontSize: 14 }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 800,
                    color: BRAND_BLUE,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  Profile Criteria
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                  gap: 2.5,
                }}
              >
                {COMMUNITY_PROFILE_FIELDS.map((field) => renderProfileField(field))}
              </Box>
            </Box>
          ) : hasCommunityProfile ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                gap: 1.75,
              }}
            >
              {COMMUNITY_PROFILE_FIELDS.map((field) => {
                const FieldIcon = field.icon;
                const value = communityProfile[field.key];
                const displayValue = formatProfileDisplayValue(field, value);
                return (
                  <Box
                    key={field.key}
                    sx={{
                      border: "1px solid #edf1f6",
                      borderRadius: 2,
                      p: 2,
                      backgroundColor: "#fbfcfe",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: 1.5,
                          display: "grid",
                          placeItems: "center",
                          backgroundColor: `${field.accent}1A`,
                          color: field.accent,
                          flexShrink: 0,
                        }}
                      >
                        <FieldIcon sx={{ fontSize: 16 }} />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 800,
                          color: "#7a8fa8",
                          textTransform: "uppercase",
                          letterSpacing: "0.02em",
                        }}
                      >
                        {field.label}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.88rem",
                        fontWeight: 600,
                        color: displayValue !== "-" ? BRAND_GREEN : "#b8c2d1",
                        lineHeight: 1.5,
                      }}
                    >
                      {displayValue !== "-" ? displayValue : "Not set"}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                border: "1.5px dashed #c5d0de",
                borderRadius: 2,
                py: 5,
                px: 3,
                textAlign: "center",
                backgroundColor: "#f7f9fc",
              }}
            >
              <HubIcon sx={{ fontSize: 36, color: "#c5d0de", mb: 1 }} />
              <Typography sx={{ fontSize: "1rem", fontWeight: 800, color: BRAND_BLUE, mb: 0.75 }}>
                No target exhibitor profile yet.
              </Typography>
              <Typography sx={{ fontSize: "0.86rem", color: "#7a8fa8", mb: 2.5, maxWidth: 420, mx: "auto" }}>
                Add target industries, geographies, and company size preferences so exhibitors can be scored.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon sx={{ fontSize: 18 }} />}
                onClick={handleEditProfile}
                sx={PRIMARY_BUTTON_SX}
              >
                Create Profile
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e8edf4", gap: 1 }}>
          {profileEditing ? (
            <>
              <Button
                variant="outlined"
                onClick={() => setProfileEditing(false)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: BRAND_BLUE,
                  borderColor: BRAND_BLUE,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<TaskAltIcon sx={{ fontSize: 17 }} />}
                onClick={handleSaveProfile}
                sx={PRIMARY_BUTTON_SX}
              >
                Save Profile
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={handleCloseProfileModal}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: BRAND_BLUE,
                  borderColor: BRAND_BLUE,
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                startIcon={<EditIcon sx={{ fontSize: 17 }} />}
                onClick={handleEditProfile}
                sx={PRIMARY_BUTTON_SX}
              >
                {hasCommunityProfile ? "Edit Profile" : "Create Profile"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <UploadExhibitorListModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onImport={handleExhibitorImport}
      />
      <Drawer
        anchor="right"
        open={Boolean(selectedExhibitor)}
        onClose={() => setSelectedExhibitorId(null)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 520 },
            maxWidth: "100%",
            borderLeft: "1px solid #dce4ef",
          },
        }}
      >
        {selectedExhibitor && (
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column", backgroundColor: "#ffffff" }}>
            <Box
              sx={{
                px: 3,
                py: 2.25,
                borderBottom: "1px solid #e8edf4",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: BRAND_BLUE }}>
                  {selectedExhibitor.companyName || "Unnamed Exhibitor"}
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: "#7a8fa8", mt: 0.35 }}>
                  Exhibitor workspace
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedExhibitorId(null)} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box sx={{ px: 3, py: 2.5, overflow: "auto", flex: 1 }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2.5 }}>
                <Chip
                  label={selectedExhibitor.tier || "Unscored"}
                  size="small"
                  sx={{
                    height: 24,
                    fontWeight: 800,
                    backgroundColor:
                      selectedExhibitor.tier === "Excluded"
                        ? "rgba(211,47,47,0.12)"
                        : selectedExhibitor.tier === "Tier A"
                          ? "rgba(100,131,21,0.12)"
                          : "rgba(0,51,108,0.08)",
                    color:
                      selectedExhibitor.tier === "Excluded"
                        ? "#D32F2F"
                        : selectedExhibitor.tier === "Tier A"
                          ? BRAND_GREEN
                          : BRAND_BLUE,
                  }}
                />
                <Chip
                  label={selectedExhibitor.isDuplicate ? "Possible Duplicate" : "Unique"}
                  size="small"
                  sx={{
                    height: 24,
                    fontWeight: 800,
                    backgroundColor: selectedExhibitor.isDuplicate
                      ? "rgba(183,121,31,0.14)"
                      : "rgba(100,131,21,0.12)",
                    color: selectedExhibitor.isDuplicate ? "#B7791F" : BRAND_GREEN,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.5,
                  mb: 3,
                }}
              >
                {EXHIBITOR_DETAIL_FIELDS.map((field) => (
                  <Box
                    key={field.key}
                    sx={{
                      gridColumn: field.wide ? "1 / -1" : "auto",
                      border: "1px solid #edf1f6",
                      borderRadius: 2,
                      p: 1.5,
                      backgroundColor: "#f7f9fc",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: BRAND_GREEN, mb: 0.5 }}>
                      {field.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.88rem",
                        fontWeight: 600,
                        color: BRAND_BLUE,
                        whiteSpace: field.wide ? "pre-wrap" : "normal",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {formatExhibitorDetailValue(field, selectedExhibitor)}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Typography sx={{ fontSize: "0.9rem", fontWeight: 800, color: BRAND_BLUE, mb: 1 }}>
                Actions
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
                {EXHIBITOR_DETAIL_ACTIONS.map((action) => (
                  <Button
                    key={action.label}
                    variant="outlined"
                    startIcon={
                      action.label === "Generate Brief" &&
                      briefGenerationExhibitorId === selectedExhibitor.id
                        ? <CircularProgress size={16} color="inherit" />
                        : action.icon
                    }
                    disabled={
                      Boolean(briefGenerationExhibitorId) ||
                      !["Generate Brief", "Generate Outreach Draft"].includes(action.label)
                    }
                    onClick={
                      action.label === "Generate Brief"
                        ? () => handleGenerateBrief(selectedExhibitor)
                        : action.label === "Generate Outreach Draft"
                          ? () => handleGenerateDraft(selectedExhibitor)
                        : undefined
                    }
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontWeight: 800,
                      minHeight: 44,
                      color: ["Generate Brief", "Generate Outreach Draft"].includes(action.label)
                        ? BRAND_BLUE
                        : undefined,
                      borderColor: ["Generate Brief", "Generate Outreach Draft"].includes(action.label)
                        ? BRAND_BLUE
                        : undefined,
                    }}
                  >
                    {action.label === "Generate Brief" &&
                    briefGenerationExhibitorId === selectedExhibitor.id
                      ? "Generating Brief..."
                      : action.label}
                    {!["Generate Brief", "Generate Outreach Draft"].includes(action.label)
                      ? " - Coming soon"
                      : ""}
                  </Button>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>
      <Dialog
        open={Boolean(selectedBrief)}
        onClose={() => setSelectedBriefId(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid #dce4ef`,
          },
        }}
      >
        {selectedBrief && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                borderBottom: "1px solid #e8edf4",
                px: 3,
                py: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: BRAND_BLUE }}>
                  {selectedBrief.companyName}
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: "#7a8fa8", mt: 0.35 }}>
                  AI company brief generated {formatDateTime(selectedBrief.generatedAt)}
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedBriefId(null)} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 3, pt: 3.25, pb: 2.5, maxHeight: "68vh", overflowY: "auto" }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2.5 }}>
                <Chip
                  label={selectedBrief.tier}
                  size="small"
                  sx={{
                    height: 24,
                    backgroundColor: "rgba(0,51,108,0.08)",
                    color: BRAND_BLUE,
                    fontWeight: 800,
                  }}
                />
                <Chip
                  label={`Score: ${Number.isFinite(selectedBrief.score) ? selectedBrief.score : "-"}`}
                  size="small"
                  sx={{
                    height: 24,
                    backgroundColor: "rgba(100,131,21,0.12)",
                    color: BRAND_GREEN,
                    fontWeight: 800,
                  }}
                />
                <Chip
                  label={selectedBrief.status}
                  size="small"
                  sx={{
                    height: 24,
                    backgroundColor: "#f0f2f5",
                    color: "#5a6a80",
                    fontWeight: 800,
                  }}
                />
                <Chip
                  label={`Generated: ${formatDateTime(selectedBrief.generatedAt)}`}
                  size="small"
                  sx={{
                    height: 24,
                    backgroundColor: "#f0f2f5",
                    color: "#5a6a80",
                    fontWeight: 800,
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {BRIEF_SECTIONS.map((section) => (
                  <Box
                    key={section.key}
                    sx={{
                      border: "1px solid #edf1f6",
                      borderRadius: 2,
                      backgroundColor: "#f7f9fc",
                      p: 2,
                    }}
                  >
                    <Typography sx={{ fontSize: "0.86rem", fontWeight: 800, color: BRAND_BLUE, mb: 0.75 }}>
                      {section.label}
                    </Typography>
                    <Typography sx={{ fontSize: "0.9rem", color: "#3a4a5c", whiteSpace: "pre-wrap" }}>
                      {selectedBrief.sections?.[section.key] || "-"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e8edf4" }}>
              <Button
                variant="outlined"
                startIcon={
                  selectedBriefExhibitor &&
                  briefGenerationExhibitorId === selectedBriefExhibitor.id
                    ? <CircularProgress size={16} color="inherit" />
                    : <ReplayIcon />
                }
                disabled={!selectedBriefExhibitor || Boolean(briefGenerationExhibitorId)}
                onClick={() => handleGenerateBrief(selectedBriefExhibitor)}
                sx={{
                  textTransform: "none",
                  fontWeight: 800,
                  color: BRAND_BLUE,
                  borderColor: BRAND_BLUE,
                }}
              >
                {selectedBriefExhibitor &&
                briefGenerationExhibitorId === selectedBriefExhibitor.id
                  ? "Generating Brief..."
                  : "Regenerate Brief"}
              </Button>
              <Button
                variant="contained"
                onClick={() => setSelectedBriefId(null)}
                sx={PRIMARY_BUTTON_SX}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Dialog
        open={Boolean(selectedDraft)}
        onClose={() => setSelectedDraftId(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid #dce4ef`,
          },
        }}
      >
        {selectedDraft && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                borderBottom: "1px solid #e8edf4",
                px: 3,
                py: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: BRAND_BLUE }}>
                  {selectedDraft.companyName}
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", color: "#7a8fa8", mt: 0.35 }}>
                  {selectedDraft.draftType} generated {formatDateTime(selectedDraft.generatedAt)}
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedDraftId(null)} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 3, py: 2.5 }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2.5 }}>
                <Chip
                  label={selectedDraft.status}
                  size="small"
                  sx={{
                    height: 24,
                    backgroundColor: "rgba(0,51,108,0.08)",
                    color: BRAND_BLUE,
                    fontWeight: 800,
                  }}
                />
                <Chip
                  label={selectedDraft.draftType}
                  size="small"
                  sx={{
                    height: 24,
                    backgroundColor: "rgba(100,131,21,0.12)",
                    color: BRAND_GREEN,
                    fontWeight: 800,
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {[
                  ["Subject Line 1", selectedDraft.subjectLine1],
                  ["Subject Line 2", selectedDraft.subjectLine2],
                  ["Email Body", selectedDraft.emailBody],
                ].map(([label, value]) => (
                  <Box
                    key={label}
                    sx={{
                      border: "1px solid #edf1f6",
                      borderRadius: 2,
                      backgroundColor: "#f7f9fc",
                      p: 2,
                    }}
                  >
                    <Typography sx={{ fontSize: "0.86rem", fontWeight: 800, color: BRAND_BLUE, mb: 0.75 }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: "0.9rem", color: "#3a4a5c", whiteSpace: "pre-wrap" }}>
                      {value || "-"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                px: 3,
                py: 2,
                borderTop: "1px solid #e8edf4",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {["Approve", "Reject", "Mark Sent Manually"].map((action) => (
                  <Button key={action} variant="outlined" disabled sx={{ textTransform: "none", fontWeight: 800 }}>
                    {action} - Coming soon
                  </Button>
                ))}
              </Box>
              <Button
                variant="contained"
                onClick={() => setSelectedDraftId(null)}
                sx={PRIMARY_BUTTON_SX}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Snackbar
        open={uploadSuccessOpen}
        autoHideDuration={4500}
        onClose={() => setUploadSuccessOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setUploadSuccessOpen(false)}
          sx={{ fontWeight: 600 }}
        >
          Exhibitor list saved to this workspace.
        </Alert>
      </Snackbar>
      <Snackbar open={Boolean(uploadError)} autoHideDuration={6500} onClose={() => setUploadError("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="error" variant="filled" onClose={() => setUploadError("")}>{uploadError}</Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(briefGenerationError)}
        autoHideDuration={6500}
        onClose={() => setBriefGenerationError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setBriefGenerationError("")}
          sx={{ fontWeight: 600 }}
        >
          {briefGenerationError}
        </Alert>
      </Snackbar>
    </Box>
  );
}
